<template>
    <component
        :is="is.is"
        v-if="is"
        v-bind="is.bind"
    />
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent } from 'vue'
import type {
    Color,
    ComponentFragment,
    ShopWithConfidenceProductFragment,
} from '@/graphql/api'
import type { Props as RelatedProductProps } from '@/components/rings/RelatedProducts.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'
import { getComponent } from '@/types/vue.ts'
import { assertNever } from '@/helpers'

const ComponentContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContent.vue')
)
const CardComponent = defineAsyncComponent(
    () => import('@/components/cms/component/CardComponent.vue')
)
const ComponentTextOverImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextOverImage.vue')
)
const ComponentRoundedImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentRoundedImage.vue')
)
const ComponentRoundedImageContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentRoundedImageContent.vue')
)
const ComponentReviewCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentReviewCarousel.vue')
)
const ComponentCustomProductCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentCustomProductCarousel.vue')
)
const ComponentMainCover = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentMainCover.vue')
)
const DebugComponent = defineAsyncComponent(
    () => import('@/components/cms/component/DebugComponent.vue')
)
const ComponentVideo = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentVideo.vue')
)
const ComponentVideoWithContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentVideoWithContent.vue')
)
const ComponentInstagramPosts = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentInstagramPosts.vue')
)
const ComponentLogos = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogos.vue')
)
const ComponentLogosCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogosCarousel.vue')
)
const ComponentTextWithImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextWithImage.vue')
)
const ComponentGalleryCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGalleryCarousel.vue')
)
const ComponentCardCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/carousel/card/ComponentCardCarousel.vue')
)
const ComponentFullWidthImage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentFullWidthImage.vue')
)
const ComponentSideBySideImages = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentSideBySideImages.vue')
)
const ComponentArticlePreviews = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentArticlePreviews.vue')
)
const ComponentInstagramWithContent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentInstagramWithContent.vue')
)
const ComponentBannerTitle = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentBannerTitle.vue')
)
const ComponentQuote = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentQuote.vue')
)
const ComponentBannerSkinny = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentBannerSkinny.vue')
)
const ComponentGlobalSpacer = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGlobalSpacer.vue')
)
const ComponentTextWithImageCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentTextWithImageCarousel.vue')
)
const ComponentContentWithGallery = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContentWithGallery.vue')
)
const ComponentIconCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentIconCarousel.vue')
)
const ComponentFaq = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentFaq.vue')
)
const ComponentContentTwoColumns = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentContentTwoColumns.vue')
)
const ComponentTable = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTable.vue')
)
const ComponentArticleGrid = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentArticleGrid.vue')
)
const ComponentOnThisPage = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentOnThisPage.vue')
)
const ComponentCatalogueProductCarousel = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentCatalogueProductCarousel.vue')
)
const RelatedProducts = defineAsyncComponent(
    () => import('@/components/rings/RelatedProducts.vue')
)
const ComponentGlobalComponent = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentGlobalComponent.vue')
)
const ComponentTextCarousel = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentTextCarousel.vue')
)
const ComponentGalleryWithEnquiry = defineAsyncComponent(
    () =>
        import('@/components/cms/component/gallery-with-enquiry/ComponentGalleryWithEnquiry.vue')
)
const ComponentSquareBoxes = defineAsyncComponent(
    () =>
        import('@/components/cms/component/square-boxes/ComponentSquareBoxes.vue')
)
const ComponentShopWithConfidence = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentShopWithConfidence.vue')
)
const ComponentWarrantyActivation = defineAsyncComponent(
    () =>
        import('@/components/cms/component/ComponentWarrantyActivationForm.vue')
)
const ComponentJobs = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentJobs.vue')
)

const ComponentLogosWithText = defineAsyncComponent(
    () => import('@/components/cms/component/ComponentLogosWithText.vue')
)

const props = defineProps<{
    data: ComponentFragment
    background: Color
    hasEagerImage?: boolean
    relatedProducts?: RelatedProductProps
    product?: ShopWithConfidenceProductFragment
    pageType: PageType
}>()

const is = computed(() => {
    const data = props.data

    switch (data.__typename) {
        case 'ComponentContent':
            return getComponent(ComponentContent, {
                data,
                pageType: props.pageType,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentCard':
            return getComponent(CardComponent, {
                data,
                pageType: props.pageType,
            })
        case 'ComponentCustomProductCarousel':
            return getComponent(ComponentCustomProductCarousel, { data })
        case 'ComponentCatalogueProductCarousel':
            return getComponent(ComponentCatalogueProductCarousel, { data })
        case 'ComponentReviewCarousel':
            return getComponent(ComponentReviewCarousel, { data })
        case 'ComponentRoundedImage':
            return getComponent(ComponentRoundedImage, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentRoundedImageContent':
            return getComponent(ComponentRoundedImageContent, {
                data,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentTextOverImage':
            return getComponent(ComponentTextOverImage, {
                data,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentMainCover':
            return getComponent(ComponentMainCover, {
                data,
                pageType: props.pageType,
            })
        case 'ComponentVideo':
            return getComponent(ComponentVideo, { data })
        case 'ComponentVideoWithContent':
            return getComponent(ComponentVideoWithContent, {
                data,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentInstagramPosts':
            return getComponent(ComponentInstagramPosts, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentLogos':
            return getComponent(ComponentLogos, { data })
        case 'ComponentLogosCarousel':
            return getComponent(ComponentLogosCarousel, { data })
        case 'ComponentTextWithImage':
            return getComponent(ComponentTextWithImage, {
                data,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentGalleryCarousel':
            return getComponent(ComponentGalleryCarousel, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentCardCarousel':
            return getComponent(ComponentCardCarousel, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentFullWidthImage':
            return getComponent(ComponentFullWidthImage, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentSideBySideImages':
            return getComponent(ComponentSideBySideImages, {
                data,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentArticlePreviews':
            return getComponent(ComponentArticlePreviews, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentInstagramWithContent':
            return getComponent(ComponentInstagramWithContent, {
                data,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentBannerTitle':
            return getComponent(ComponentBannerTitle, { data })
        case 'ComponentBannerSkinny':
            return getComponent(ComponentBannerSkinny, { data })
        case 'ComponentQuote':
            return getComponent(ComponentQuote, { data })
        case 'ComponentGlobalSpacer':
            return getComponent(ComponentGlobalSpacer, { data })
        case 'ComponentTextWithImageCarousel':
            return getComponent(ComponentTextWithImageCarousel, {
                data,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentContentWithGallery':
            return getComponent(ComponentContentWithGallery, {
                data,
                background: props.background,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentIconCarousel':
            return getComponent(ComponentIconCarousel, {
                data,
                background: props.background,
            })
        case 'ComponentTextCarousel':
            return getComponent(ComponentTextCarousel, {
                data,
                background: props.background,
            })
        case 'ComponentFaq':
            return getComponent(ComponentFaq, {
                data,
                pageType: props.pageType,
            })
        case 'ComponentContentTwoColumns':
            return getComponent(ComponentContentTwoColumns, {
                data,
                pageType: props.pageType,
            })
        case 'ComponentTable':
            return getComponent(ComponentTable, { data })
        case 'ComponentArticleGrid':
            return getComponent(ComponentArticleGrid, {
                data,
                hasEagerImage: props.hasEagerImage,
            })
        case 'ComponentOnThisPage':
            return getComponent(ComponentOnThisPage, { data })
        case 'ComponentRelatedProducts':
            if (!props.relatedProducts?.products.length) {
                return undefined
            }
            return getComponent(RelatedProducts, {
                products: props.relatedProducts.products,
            })
        case 'ComponentGlobalComponent':
            return getComponent(ComponentGlobalComponent, {
                data,
                relatedProducts: props.relatedProducts,
                product: props.product,
                pageType: props.pageType,
            })
        case 'ComponentGalleryWithEnquiry':
            return getComponent(ComponentGalleryWithEnquiry, { data })
        case 'ComponentSquareBoxes':
            return getComponent(ComponentSquareBoxes, {
                data,
                hasEagerImage: props.hasEagerImage,
                pageType: props.pageType,
            })
        case 'ComponentShopWithConfidence':
            if (!props.product) {
                return undefined
            }
            return getComponent(ComponentShopWithConfidence, {
                data,
                background: props.background,
                product: props.product as ShopWithConfidenceProductFragment,
                pageType: props.pageType,
            })
        case 'ComponentWarrantyActivationForm':
            return getComponent(ComponentWarrantyActivation, {})
        case 'ComponentJobs':
            return getComponent(ComponentJobs, {
                data,
                pageType: props.pageType,
            })
        case 'ComponentLogosWithText':
            return getComponent(ComponentLogosWithText, { data })
        default:
            return assertNever(data, getComponent(DebugComponent, { data }))
    }
})
</script>
